/**
 * Prod answer-shape baseline (QA read-only counts, Oct 2026) — the Vite
 * "already answered" check must treat ALL of these as answered:
 *   (a) `{ selection: <option id> }`;
 *   (b) `{ selection: <exact label> }` (4,650 prod answers);
 *   (c) LEGACY `{ answer, category, question, questionMappingId }` with no
 *       `selection` (234,282 prod answers on {id,label} questions);
 *   (d) bare JSON strings (2,836);
 *   (e) `{ text }`-only answers, which store the label by design (~1.14M).
 *
 * Any non-empty answer in ANY shape counts as answered and is never
 * re-asked. Unmatched labels still count — answered-ness is
 * presence-based, never membership-based. Case/whitespace-normalised label
 * matching is ONLY for dependency/skip rules (`answerMatchesTargets`).
 *
 * Single source of truth: `docs/context/FEATURE_SPECS/question-equivalence.json`
 * (byte-identical copy of the reklaim-services contract, version 1). The
 * answered-ness rule below is a verbatim port of the sibling
 * `hasAnswerValue` (`packages/supabase/consumer/answer-shapes.ts` on
 * `cursor/rek-equivalent-answers-panels-3039`): envelope fields
 * `selection AnswerId answer text value label` in lookup order, scalars
 * (incl. booleans) by structural non-emptiness, arrays by ANY non-empty
 * entry, objects with none of the fields as unanswered, `{skipped: true}`
 * never an answer. The edge copy lives in
 * `supabase/functions/_shared/survey-answer-validation.ts`
 * (`attributeValueCountsAsAnswer`); `ANSWER_SHAPE_FIXTURES` carries the same
 * cases on both sides — keep the ids, shapes, and values identical.
 */

/**
 * Envelope fields that can carry the member's answer, in lookup order.
 * `selection` is the current writer; `answerId`/`answer` are the legacy
 * writers; `text`/`value`/`label` cover `{text}`-only and bare-label rows.
 * Identical to the sibling `ANSWER_FIELDS`.
 */
const ANSWER_CARRIERS = ['selection', 'answerId', 'answer', 'text', 'value', 'label'] as const;

/**
 * Extract the answer value from a `user_knowledge.attribute_value` cell in
 * any prod shape: current `{ selection }`, legacy `{ answerId }` /
 * `{ answer }`, `{ text }`-only, `{ value }` / `{ label }` rows, a bare
 * string (incl. JSON-encoded), or a scalar. Returns `undefined` when no
 * carrier is present. Used for dependency/skip value resolution — never for
 * answered-ness (see `attributeValueCountsAsAnswer`).
 */
export function extractAnswerValue(attributeValue: unknown): unknown {
  if (attributeValue == null) return undefined;
  if (typeof attributeValue === 'string') {
    const trimmed = attributeValue.trim();
    if (!trimmed) return undefined;
    // A JSON-encoded envelope resolves to its carrier so dependency/skip
    // matching sees the option value, not the encoding.
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return extractAnswerValue(parsed);
      }
    } catch {
      // Not JSON — the plain string is the answer.
    }
    return attributeValue;
  }
  if (typeof attributeValue === 'number' || typeof attributeValue === 'boolean') {
    return attributeValue;
  }
  if (typeof attributeValue !== 'object' || Array.isArray(attributeValue)) {
    return Array.isArray(attributeValue) && attributeValue.length > 0 ? attributeValue : undefined;
  }
  const obj = attributeValue as Record<string, unknown>;
  for (const key of ANSWER_CARRIERS) {
    const carrier = obj[key];
    if (carrier == null) continue;
    return carrier;
  }
  return undefined;
}

/** Structural scalar non-emptiness — identical to the sibling `hasScalarAnswer`. */
function hasScalarAnswer(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  return false;
}

/**
 * Whether an extracted (or plain) answer value is non-empty. Same recursive
 * rule as `attributeValueCountsAsAnswer`: scalars by structural
 * non-emptiness (booleans count), arrays by ANY non-empty entry, carrier
 * objects by ANY non-empty carrier. Unknown envelopes stay unanswered — a
 * conservative miss re-asks once, while treating metadata as an answer
 * would hide a question the member never answered.
 */
export function answerValueIsNonEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return hasScalarAnswer(value);
  }
  if (Array.isArray(value)) {
    return value.some((entry) => answerValueIsNonEmpty(entry));
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return ANSWER_CARRIERS.some((field) => field in obj && answerValueIsNonEmpty(obj[field]));
  }
  return false;
}

/**
 * Whether a raw `attribute_value` cell counts as an answer in any prod
 * shape. Verbatim port of the sibling `hasAnswerValue` — any non-empty
 * answer in any shape counts, never re-asked. Skip markers
 * (`{skipped: true}`) carry no answer field and stay unanswered.
 */
export function attributeValueCountsAsAnswer(attributeValue: unknown): boolean {
  return answerValueIsNonEmpty(attributeValue);
}

/**
 * Case/whitespace-insensitive label normaliser for DEPENDENCY / SKIP rules
 * only. Rules (mirrored on all three sides): trim, collapse inner
 * whitespace runs to one space, lowercase.
 */
export function normalizeAnswerLabel(value: unknown): string {
  return String(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Strip legacy "__N" suffixes (e.g. "Toyota__0" → "Toyota"). */
export function stripAnswerOptionSuffix(v: string): string {
  return v.replace(/__\d+$/, '');
}

/**
 * Whether a stored answer matches any target/enabling value: exact first
 * (existing behaviour, unchanged), then `__N`-stripped exact, then
 * case/whitespace-normalised label equality on both sides. Only used for
 * dependency/skip rules — never for answered-ness, never for submit
 * membership.
 */
export function answerMatchesTargets(selection: unknown, targets: readonly string[]): boolean {
  const values = Array.isArray(selection) ? selection : [selection];
  return values.some((v) => {
    if (typeof v !== 'string' && typeof v !== 'number') return false;
    const s = String(v);
    if ((targets as readonly string[]).includes(s)) return true;
    if ((targets as readonly string[]).includes(stripAnswerOptionSuffix(s))) return true;
    const normalized = normalizeAnswerLabel(s);
    return (targets as readonly string[]).some((t) => normalizeAnswerLabel(t) === normalized);
  });
}

export interface AnswerShapeFixture {
  /** Fixture id — identical across all three mirror sides. */
  id: string;
  /** Prod shape bucket (a)–(e), or `empty-*` for must-NOT-count cases. */
  shape: string;
  attribute_value: unknown;
  /** Expected `attributeValueCountsAsAnswer` result. */
  countsAsAnswer: boolean;
}

/**
 * Canonical per-shape fixtures — one row per prod shape plus the empty
 * cases that must NOT count. The Deno copy lives in
 * `supabase/functions/_shared/survey-answer-validation-shapes_test.ts` with
 * identical ids, shapes, and values; the sibling pins the same rules via its
 * own cases in `packages/supabase/consumer/answer-shapes.test.ts`.
 */
export const ANSWER_SHAPE_FIXTURES: readonly AnswerShapeFixture[] = [
  { id: 'shape-a-option-id', shape: 'a', attribute_value: { selection: 'opt_0' }, countsAsAnswer: true },
  { id: 'shape-a-multi', shape: 'a', attribute_value: { selection: ['opt_0', 'opt_1'] }, countsAsAnswer: true },
  // ANY non-empty array entry counts (sibling `some` rule) — a trailing
  // blank entry does not un-answer the row.
  { id: 'shape-a-array-partial-blank', shape: 'a', attribute_value: { selection: ['opt_0', '  '] }, countsAsAnswer: true },
  { id: 'shape-a-boolean', shape: 'a', attribute_value: { selection: true }, countsAsAnswer: true },
  { id: 'shape-b-exact-label', shape: 'b', attribute_value: { selection: 'Central Air' }, countsAsAnswer: true },
  {
    id: 'shape-c-legacy-answer',
    shape: 'c',
    attribute_value: {
      answer: 'Central Air',
      category: 'home',
      question: 'What type of AC?',
      questionMappingId: 'aaaabbbb-cccc-dddd-eeee-ffffffffffff',
    },
    countsAsAnswer: true,
  },
  { id: 'shape-d-bare-string', shape: 'd', attribute_value: 'Central Air', countsAsAnswer: true },
  {
    id: 'shape-d-double-encoded',
    shape: 'd',
    attribute_value: JSON.stringify({ selection: 'Central Air' }),
    countsAsAnswer: true,
  },
  { id: 'shape-e-text-only', shape: 'e', attribute_value: { text: 'Central Air' }, countsAsAnswer: true },
  { id: 'shape-e-value-carrier', shape: 'e', attribute_value: { value: 'Central Air' }, countsAsAnswer: true },
  { id: 'shape-e-label-carrier', shape: 'e', attribute_value: { label: 'Central Air' }, countsAsAnswer: true },
  { id: 'empty-selection-blank', shape: 'empty', attribute_value: { selection: '' }, countsAsAnswer: false },
  { id: 'empty-selection-spaces', shape: 'empty', attribute_value: { selection: '   ' }, countsAsAnswer: false },
  { id: 'empty-selection-array', shape: 'empty', attribute_value: { selection: [] }, countsAsAnswer: false },
  { id: 'empty-selection-array-all-blank', shape: 'empty', attribute_value: { selection: ['  '] }, countsAsAnswer: false },
  { id: 'empty-null-cell', shape: 'empty', attribute_value: null, countsAsAnswer: false },
  { id: 'empty-bare-blank-string', shape: 'empty', attribute_value: '   ', countsAsAnswer: false },
  { id: 'empty-carrierless-object', shape: 'empty', attribute_value: { skipped: true }, countsAsAnswer: false },
  { id: 'empty-text-blank', shape: 'empty', attribute_value: { text: '  ' }, countsAsAnswer: false },
  { id: 'empty-legacy-answer-blank', shape: 'empty', attribute_value: { answer: '', category: 'home' }, countsAsAnswer: false },
];

export interface LabelMatchFixture {
  id: string;
  stored: unknown;
  targets: readonly string[];
  matches: boolean;
}

/** Canonical dependency/skip matching cases — identical on all mirror sides. */
export const LABEL_MATCH_FIXTURES: readonly LabelMatchFixture[] = [
  { id: 'label-exact', stored: 'Central Air', targets: ['Central Air'], matches: true },
  { id: 'label-case', stored: 'central air', targets: ['Central Air'], matches: true },
  { id: 'label-spacing', stored: '  Central   Air ', targets: ['Central Air'], matches: true },
  { id: 'label-target-case', stored: 'Central Air', targets: ['central air'], matches: true },
  { id: 'label-suffix', stored: 'Toyota__0', targets: ['Toyota'], matches: true },
  { id: 'label-array-one-matches', stored: ['No', 'central air'], targets: ['Central Air'], matches: true },
  { id: 'label-no-match', stored: 'Yes', targets: ['No', 'Maybe'], matches: false },
  { id: 'label-blank-vs-targets', stored: '', targets: ['No'], matches: false },
];
